import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import IntegrationCapabilities from "@/components/IntegrationCapabilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Receipt, KeyRound, Eye, EyeOff, Loader2, Save, Trash2,
  CheckCircle2, AlertTriangle, ExternalLink, RefreshCw, FileText,
  Users, BookOpen,
} from "lucide-react";

/**
 * Xero (master) integration page.
 *
 * Master pattern: Iceberg's *own* Xero account is connected once. Per the
 * user's instruction (2026-06-22) we do NOT pull client books — just our
 * own. Per-client Xero would require switching to the playbook's
 * multi-tenant model.
 */
export default function XeroMasterPage() {
  // ── Creds ───────────────────────────────────────────────────────
  const [creds, setCreds] = useState(null);
  const [savingCreds, setSavingCreds] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [redirectUri, setRedirectUri] = useState("");
  const [scopes, setScopes] = useState("");
  // ── Connection ──────────────────────────────────────────────────
  const [conn, setConn] = useState({ connected: false });
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  // ── Data preview ────────────────────────────────────────────────
  const [invoices, setInvoices] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [loadingData, setLoadingData] = useState("");

  const loadAll = useCallback(async () => {
    try {
      const [c, k] = await Promise.all([
        api.get("/integrations/xero/creds"),
        api.get("/integrations/xero/connection"),
      ]);
      setCreds(c.data);
      setConn(k.data);
      setClientId(c.data.client_id || "");
      setRedirectUri(c.data.redirect_uri ||
        `${window.location.origin}/integrations/xero/callback`);
      setScopes(c.data.scopes || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Xero state");
    }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Surface OAuth callback result from URL params
  useEffect(() => {
    const u = new URL(window.location.href);
    if (u.searchParams.get("xero_connected") === "1") {
      toast.success(`Xero connected as ${u.searchParams.get("tenant") || "tenant"}`);
      u.searchParams.delete("xero_connected");
      u.searchParams.delete("tenant");
      window.history.replaceState({}, "", u.toString());
      loadAll();
    } else if (u.searchParams.get("xero_error")) {
      toast.error(`Xero: ${u.searchParams.get("xero_error")}`, { duration: 10000 });
      u.searchParams.delete("xero_error");
      window.history.replaceState({}, "", u.toString());
    }
  }, [loadAll]);

  const saveCreds = async () => {
    if (!clientId.trim() || !clientSecret.trim() || !redirectUri.trim()) {
      toast.error("Client ID, secret and redirect URI are required."); return;
    }
    setSavingCreds(true);
    try {
      await api.put("/integrations/xero/creds", {
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
        redirect_uri: redirectUri.trim(),
        scopes: scopes.trim() || null,
      });
      toast.success("Xero credentials saved");
      setClientSecret("");
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingCreds(false); }
  };

  const deleteCreds = async () => {
    if (!window.confirm("Delete Xero credentials & disconnect? This wipes the master OAuth.")) return;
    try {
      await api.delete("/integrations/xero/creds");
      toast.success("Xero credentials deleted");
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      // Pass the browser's current origin so the backend uses the SAME
      // host for both the authorize URL and the token exchange. This
      // lets the same Xero app registration work from preview + live
      // (and any future domain) as long as each URL is added to the
      // Xero app's redirect list.
      const { data } = await api.get("/integrations/xero/oauth/start", {
        params: { origin: window.location.origin },
      });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Xero OAuth");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Xero?")) return;
    try {
      await api.delete("/integrations/xero/connection");
      toast.success("Disconnected");
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Disconnect failed"); }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/integrations/xero/test");
      toast.success(`Test passed${data.organisation_name ? ` · ${data.organisation_name}` : ""}`);
      await loadAll();
    } catch (e) {
      toast.error(`Test failed: ${e.response?.data?.detail || e.message}`);
      await loadAll();
    } finally { setTesting(false); }
  };

  const fetchData = async (kind) => {
    setLoadingData(kind);
    try {
      const url = ({
        invoices: "/integrations/xero/invoices",
        contacts: "/integrations/xero/contacts",
        pnl: "/integrations/xero/profit-and-loss",
      })[kind];
      const { data } = await api.get(url);
      if (kind === "invoices") setInvoices(data.invoices || []);
      if (kind === "contacts") setContacts(data.contacts || []);
      if (kind === "pnl") setPnl(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Fetch failed"); }
    finally { setLoadingData(""); }
  };

  if (!creds) {
    return (
      <div className="p-8" data-testid="xero-loading">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-6xl" data-testid="xero-master-page">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#13B5EA]/10">
          <Receipt className="h-6 w-6 text-[#13B5EA]" />
        </div>
        <div>
          <div className="label-overline">XERO · MASTER ACCOUNT</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Xero</h1>
          <p className="text-sm text-zinc-500 max-w-3xl mt-1">
            Connect Iceberg Digital&apos;s own Xero organisation. We pull invoices,
            contacts and Profit &amp; Loss for our own internal reporting — we do
            NOT access client books at this stage.
          </p>
        </div>
      </div>

      {/* ── Credentials card ── */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-display font-bold text-lg flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-zinc-500" />
              App credentials
              {creds.configured
                ? <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200">Saved</Badge>
                : <Badge variant="outline" className="rounded-sm bg-amber-50 text-amber-800 border-amber-200">Not set</Badge>}
            </div>
            <a href="https://developer.xero.com/app/manage" target="_blank" rel="noreferrer"
              className="text-xs text-[#13B5EA] inline-flex items-center gap-1 hover:underline">
              Open Xero developer portal <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <p className="text-xs text-zinc-500">
            Create an OAuth2 app at developer.xero.com, choose &quot;Auth code&quot; flow, register the redirect URI below,
            then paste the Client ID + Client Secret here.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Client ID
              <Input value={clientId} onChange={(e) => setClientId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                className="mt-1.5 font-mono text-sm" data-testid="xero-client-id" />
            </label>
            <label className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Client Secret
              <div className="relative mt-1.5">
                <Input value={clientSecret} onChange={(e) => setClientSecret(e.target.value)}
                  type={showSecret ? "text" : "password"}
                  placeholder={creds.has_secret ? "•••• already saved ····" : "Paste secret"}
                  className="font-mono text-sm pr-9" data-testid="xero-client-secret" />
                <button type="button" onClick={() => setShowSecret((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                  data-testid="xero-toggle-secret">
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>
            <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 md:col-span-2">
              Redirect URI (fallback)
              <Input value={redirectUri} onChange={(e) => setRedirectUri(e.target.value)}
                className="mt-1.5 font-mono text-sm" data-testid="xero-redirect-uri" />
              <span className="text-[10px] text-zinc-500 mt-1 block normal-case tracking-normal leading-4">
                As of Feb 2026, the app auto-uses the <b>current site&apos;s origin</b> when you click Connect — so preview <i>and</i> live can share one Xero app. This field is only a fallback for legacy callers.
                <br />
                Register <b>both</b> of these in your Xero app &rsaquo; OAuth 2.0 redirect URIs:
                <br />
                <span className="font-mono">{window.location.origin}/integrations/xero/callback</span>
                <br />
                <span className="font-mono">https://icebergdigital.com.au/integrations/xero/callback</span>
              </span>
            </label>
            <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 md:col-span-2">
              Scopes (optional)
              <Input value={scopes} onChange={(e) => setScopes(e.target.value)}
                placeholder="openid profile email accounting.transactions.read accounting.contacts.read accounting.reports.read offline_access"
                className="mt-1.5 font-mono text-[11px]" data-testid="xero-scopes" />
            </label>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" className="rounded-sm bg-[#13B5EA] hover:bg-[#0F9BC8] text-white"
              disabled={savingCreds} onClick={saveCreds} data-testid="xero-save-creds">
              {savingCreds ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                : <Save className="h-3.5 w-3.5 mr-1.5" />}
              Save credentials
            </Button>
            {creds.configured && (
              <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                onClick={deleteCreds} data-testid="xero-delete-creds">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Connection card ── */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display font-bold text-lg flex items-center gap-2">
                Connection
                {conn.connected
                  ? <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Connected
                    </Badge>
                  : <Badge variant="outline" className="rounded-sm bg-zinc-100 text-zinc-700">Not connected</Badge>}
                {conn.connected && conn.last_test_ok === true && (
                  <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200" title={conn.last_test_at}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Last test: OK
                  </Badge>
                )}
                {conn.connected && conn.last_test_ok === false && (
                  <Badge className="rounded-sm bg-red-100 text-red-800 border-red-200" title={conn.last_test_error || ""}>
                    <AlertTriangle className="h-3 w-3 mr-1" /> Last test: failed
                  </Badge>
                )}
              </div>
              {conn.connected && (
                <div className="text-sm text-zinc-700 mt-1">
                  <span className="font-semibold">{conn.organisation_name || conn.tenant_name}</span>
                  {conn.tenant_type && <span className="text-zinc-500"> · {conn.tenant_type}</span>}
                  <div className="text-[10px] text-zinc-400 font-mono mt-0.5">tenant_id: {conn.tenant_id}</div>
                  {conn.last_test_error && (
                    <div className="text-[11px] text-red-700 mt-1 font-mono">{conn.last_test_error}</div>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {conn.connected && (
                <Button size="sm" variant="outline" className="rounded-sm"
                  disabled={testing} onClick={runTest} data-testid="xero-test">
                  {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                  Test connection
                </Button>
              )}
              {conn.connected ? (
                <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                  onClick={disconnect} data-testid="xero-disconnect">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Disconnect
                </Button>
              ) : (
                <Button size="sm" className="rounded-sm bg-[#13B5EA] hover:bg-[#0F9BC8] text-white"
                  disabled={!creds.configured || connecting} onClick={connect}
                  data-testid="xero-connect">
                  {connecting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    : <ExternalLink className="h-3.5 w-3.5 mr-1.5" />}
                  Connect to Xero
                </Button>
              )}
            </div>
          </div>
          {!creds.configured && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" />
              Save app credentials above before connecting.
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Cross-tenant rollup quick link ── */}
      {conn.connected && (conn.tenants || []).length > 0 && (
        <Link to="/integrations/xero/rollup"
          className="block border border-zinc-200 rounded-sm p-4 bg-gradient-to-r from-[#13B5EA]/5 to-transparent hover:from-[#13B5EA]/10 hover:border-[#13B5EA] transition-colors"
          data-testid="xero-rollup-link">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-display font-bold text-sm">
                Cross-tenant rollup dashboard →
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                Headline AR/AP + MTD revenue/profit across every authorised
                organisation. Cached snapshot · auto-refreshes nightly at 06:50 Sydney.
              </div>
            </div>
            <div className="text-[#13B5EA] font-display font-bold text-xs">
              Open →
            </div>
          </div>
        </Link>
      )}

      {/* ── Tenants (multi-org) ── */}
      {conn.connected && (
        <Card className="rounded-sm border-zinc-200" data-testid="xero-tenants">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="font-display font-bold text-lg">
                Authorised organisations ({(conn.tenants || []).length})
              </div>
              <span className="text-[11px] text-zinc-500">
                Click an org → opens its data dashboard.
              </span>
            </div>
            {(conn.tenants || []).length === 0 ? (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                Reconnect to populate this list — your current session was created before multi-tenant support shipped.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(conn.tenants || []).map((t) => (
                  <div key={t.tenant_id}
                    className="border border-zinc-200 rounded-sm p-3 hover:border-[#13B5EA] hover:bg-[#13B5EA]/5 transition-colors"
                    data-testid={`xero-tenant-${t.tenant_id}`}>
                    <div className="font-display font-bold text-sm truncate">{t.tenant_name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                      {t.tenant_type || "ORGANISATION"} · {String(t.tenant_id).slice(0, 8)}…
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Link to={`/integrations/xero/tenants/${t.tenant_id}/bank-rec`}
                        className="text-[10px] text-[#13B5EA] font-semibold hover:underline"
                        data-testid={`xero-tenant-${t.tenant_id}-bank-rec`}>
                        Bank rec →
                      </Link>
                      <Link to={`/integrations/xero/tenants/${t.tenant_id}/invoices`}
                        className="text-[10px] text-[#13B5EA] font-semibold hover:underline"
                        data-testid={`xero-tenant-${t.tenant_id}-invoices`}>
                        Invoices &amp; bills →
                      </Link>
                      <Link to={`/integrations/xero/tenants/${t.tenant_id}/contacts`}
                        className="text-[10px] text-[#13B5EA] font-semibold hover:underline"
                        data-testid={`xero-tenant-${t.tenant_id}-contacts`}>
                        Contacts →
                      </Link>
                      <Link to={`/integrations/xero/tenants/${t.tenant_id}/reports`}
                        className="text-[10px] text-[#13B5EA] font-semibold hover:underline"
                        data-testid={`xero-tenant-${t.tenant_id}-reports`}>
                        Reports →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Data preview ── */}
      {conn.connected && (
        <Card className="rounded-sm border-zinc-200" data-testid="xero-data-preview">
          <CardContent className="p-6 space-y-4">
            <div className="font-display font-bold text-lg">Data preview</div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="rounded-sm"
                disabled={loadingData === "invoices"} onClick={() => fetchData("invoices")}
                data-testid="xero-load-invoices">
                {loadingData === "invoices"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  : <FileText className="h-3.5 w-3.5 mr-1.5" />}
                Load invoices
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm"
                disabled={loadingData === "contacts"} onClick={() => fetchData("contacts")}
                data-testid="xero-load-contacts">
                {loadingData === "contacts"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  : <Users className="h-3.5 w-3.5 mr-1.5" />}
                Load contacts
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm"
                disabled={loadingData === "pnl"} onClick={() => fetchData("pnl")}
                data-testid="xero-load-pnl">
                {loadingData === "pnl"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  : <BookOpen className="h-3.5 w-3.5 mr-1.5" />}
                Load Profit &amp; Loss
              </Button>
            </div>
            {invoices && (
              <DataTable title={`Invoices (${invoices.length})`}
                columns={["#", "Date", "Contact", "Status", "Total", "Currency"]}
                rows={invoices.map((i) => [i.invoice_number, i.date,
                  i.contact_name, i.status, i.total, i.currency])} />
            )}
            {contacts && (
              <DataTable title={`Contacts (${contacts.length})`}
                columns={["Name", "Email", "Customer?", "Supplier?"]}
                rows={contacts.map((c) => [c.name, c.email,
                  c.is_customer ? "✓" : "", c.is_supplier ? "✓" : ""])} />
            )}
            {pnl && (
              <div>
                <div className="label-overline mb-2">{pnl.title}</div>
                <div className="border border-zinc-200 rounded-sm overflow-hidden text-[12px]">
                  {(pnl.rows || []).slice(0, 40).map((r, i) => (
                    <div key={`${r[0] ?? "row"}-${i}`}
                      className="grid grid-flow-col auto-cols-fr px-3 py-1.5 border-b border-zinc-100 last:border-b-0 font-mono">
                      {r.map((c, j) => <span key={`${j}-${String(c ?? "")}`} className="truncate">{String(c ?? "")}</span>)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <IntegrationCapabilities integrationKey="xero" accent="#13B5EA" />
    </div>
  );
}

function DataTable({ title, columns, rows }) {
  return (
    <div>
      <div className="label-overline mb-2">{title}</div>
      <div className="border border-zinc-200 rounded-sm overflow-hidden text-[12px]">
        <div className="grid grid-flow-col auto-cols-fr px-3 py-2 bg-zinc-50 border-b border-zinc-200 font-display font-bold">
          {columns.map((c) => <span key={c}>{c}</span>)}
        </div>
        {rows.slice(0, 40).map((r, i) => (
          <div key={`${r[0] ?? "row"}-${i}`}
            className="grid grid-flow-col auto-cols-fr px-3 py-1.5 border-b border-zinc-100 last:border-b-0">
            {r.map((c, j) => <span key={`${j}-${String(c ?? "")}`} className="truncate font-mono">{String(c ?? "")}</span>)}
          </div>
        ))}
        {rows.length === 0 && (
          <div className="px-3 py-3 text-center text-zinc-500 italic">No rows.</div>
        )}
      </div>
    </div>
  );
}
